import { AnyAction, Dispatch, Middleware } from "redux";
import { captureException, captureMessage, emitEncounterLifecycle, logger as sentryLogger } from "./monitoring";

interface ReduxAction extends AnyAction {
  type: string;
  payload?: {
    error?: any;
    message?: string;
    [key: string]: any;
  };
  error?: any;
}

export const logger: Middleware<{}, any, Dispatch<AnyAction>> = () => (next: Dispatch<AnyAction>) => (action: ReduxAction) => {
  try {
    // Log action to Sentry for debugging (exclude globalErrors/ actions which
    // are handled error-display dispatches, not actual failures)
    if ((action.type?.includes("Error") || action.type?.includes("FAILURE")) && !action.type?.startsWith("globalErrors/")) {
      // actionType only. The payload used to ride along here and, as of the switch from
      // breadcrumbs to real logs, would be an unindexable object flattened into an attribute -
      // and the full payload already reaches Sentry through captureException/captureMessage
      // below, where scrubEvent's allowlist governs it. What is wanted from the log is the
      // countable part: which action failed, how often, for whom.
      // actionType, not action_type: SAFE_KEYS allowlists the camelCase spelling (it is the
      // same field the captureException call below puts in event.extra), and an attribute key
      // that is not in SAFE_KEYS comes back [REDACTED] - which would have left this log with
      // no queryable content at all.
      sentryLogger.warn("redux.action_error", { actionType: action.type });
    }

    // Capture Redux errors
    if (action.error) {
      captureException(action.error, {
        actionType: action.type,
        payload: action.payload,
        source: "redux",
      });
    }

    // Capture specific error messages ONLY for failing actions.
    // Exclude globalErrors/ actions -- those are handled error-display dispatches
    // from the API middleware (e.g. globalErrors/get) and should not be reported
    // to Sentry as separate events.
    const actionType = action?.type || "";
    const isGlobalErrorAction = actionType.startsWith("globalErrors/");
    const isFailureType = /(?:Fail|Error|FAILURE)/i.test(actionType);
    const isSuccessType = /Success$/i.test(actionType);
    if (!isSuccessType && !isGlobalErrorAction && isFailureType && (action.payload?.error || action.payload?.message)) {
      captureMessage(`Redux Error: ${action.type}`, {
        actionType: action.type,
        payload: action.payload,
        source: "redux",
      });
    }

    // Emit encounter lifecycle events for Sentry Performance dashboards.
    // Covers both direct encounter creation and appointment-based creation paths.
    if (action.type === "Encounter/encounterSuccess" || action.type === "Encounter/encounterByAppointmentSuccess") {
      emitEncounterLifecycle("opened");
    }
    if (action.type === "Encounter/closeEncounterSuccess") {
      emitEncounterLifecycle("closed");
    }

    next(action);
  } catch (error) {
    // Don't let logger errors break the app
    // eslint-disable-next-line no-console
    console.error("Redux logger error:", error);
    next(action);
  }
};
